import { useState } from "react"
import { Box } from "@mui/system"
import { makeStyles } from "tss-react/mui"
import { enqueueSnackbar } from "notistack"
import { useNavigate } from "react-router-dom"
import { Card, CardContent, CardMedia, Typography } from "@mui/material"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { selectEffectiveCompanyId } from "../../../../context/contextSelectors"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { getEditNewsRoute } from "../../../../../constants/routes"
import { MerchantAdminNewsQuery, deleteMerchantAdminNews } from "../merchantAdminNewsApi"
import { MerchantNewsArticle } from "../merchantNewsItemType"
import { useAppSelector } from "../../../../store/storeHooks"
import { CardDropdownMenu } from "./CardDropdownMenu"
import { Badge } from "./Badge"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"

type MerchantNewsCardProps = Readonly<{
    article: MerchantNewsArticle
}>

export function MerchantNewsCard({ article }: MerchantNewsCardProps) {
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [isImageLoaded, setIsImageLoaded] = useState(false)

    const useStyles = makeStyles()((theme) => ({
        newsGrid: {
            justifyContent: "center",
            border: "none",
            alignSelf: "flex-start",
            "&:hover": {
                transform: "scale(1.02)",
                transition: "all .3s ease-in-out",
            },
        },
        card: {
            position: "relative",
            width: "32%", // set according to gap %
            cursor: "pointer",
            [theme.breakpoints.down(1000)]: {
                width: "49%", // set according to gap %
            },

            [theme.breakpoints.down("sm")]: {
                width: "100%",
            },
        },

        cardContent: {
            "&:last-child": {
                paddingBottom: "0.75rem",
            },
            padding: "0.75rem",
        },

        chip: {
            padding: "0.1rem",
            borderRadius: 0,
            position: "absolute",
            top: 0,
            right: 0,
        },
    }))

    const { classes, cx } = useStyles()

    const handleCardClick = (id: number) => {
        navigate(getEditNewsRoute(id))
    }

    const merchantId = useAppSelector(selectEffectiveCompanyId)
    const { mutateAsync: callDeleteMerchantAdminNews } = useApiMutation(deleteMerchantAdminNews, {
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantAdminNewsQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data?.serviceResponse?.displayMessage ?? l10n.getString("news-table-delete-success-message"),
            })
        },
    })
    function handleDeleteClick() {
        if (!merchantId) {
            return
        } else {
            callDeleteMerchantAdminNews({
                merchantId: merchantId,
                articleId: article.id,
            })
        }
    }

    return (
        <Card variant="outlined" className={cx(classes.newsGrid, classes.card)}>
            {article.leadImage?.url && (
                <CardMedia
                    src={article.leadImage?.url}
                    component="img"
                    onClick={() => {
                        handleCardClick(article.id)
                    }}
                    onLoad={() => setIsImageLoaded(true)}
                    style={{ minHeight: isImageLoaded ? "auto" : "200px" }}
                />
            )}
            {Badge({ articleStatus: article?.status, className: classes.chip })}
            <CardContent
                className={cx(classes.cardContent)}
                onClick={() => {
                    handleCardClick(article.id)
                }}
            >
                <Box sx={{ position: "relative", display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h6" fontWeight={400} sx={{ lineHeight: "1.5rem" }}>
                        {article.subject}
                    </Typography>
                    <CardDropdownMenu article={article} setShowDeleteModal={setShowDeleteModal} />
                </Box>
                {article.publishDate && (
                    <Typography
                        sx={{ fontSize: 12, lineHeight: "0.7rem", paddingTop: "0.75rem" }}
                        color="text.secondary"
                        gutterBottom
                    >
                        <LocalizedStrict id="news-published" vars={{ date: article.publishDate }}>
                            <>Published: {article.publishDate}</>
                        </LocalizedStrict>
                    </Typography>
                )}
            </CardContent>
            {showDeleteModal && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("create-or-edit-merchant-article-delete")}
                    entityName={article.subject}
                    handleClick={handleDeleteClick}
                    open={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                />
            )}
        </Card>
    )
}
